import { useState, useEffect, useCallback } from "react";
import { opApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Tag, Pencil, Trash2, Loader2, Plus } from "lucide-react";

const EMPTY = { code: "", type: "percent", value: 10, min_subtotal: 0, applies_to: "all", target_id: "",
  ends_at: "", usage_limit: "", per_customer_limit: "", stackable_with_loyalty: false, active: true };

export function PromoManager({ products = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();

  const load = useCallback(() => {
    setLoading(true);
    opApi.get("/tenant/promos").then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ code: p.code, type: p.type, value: p.value, min_subtotal: p.min_subtotal || 0,
      applies_to: p.applies_to || "all", target_id: p.target_id || "",
      ends_at: p.ends_at ? p.ends_at.slice(0, 10) : "",
      usage_limit: p.usage_limit ?? "", per_customer_limit: p.per_customer_limit ?? "",
      stackable_with_loyalty: !!p.stackable_with_loyalty, active: p.active });
    setEditId(p.id); setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim()) { toast.error("Code is required."); return; }
    setSaving(true);
    const payload = {
      code: form.code.trim(), type: form.type, value: Number(form.value) || 0,
      min_subtotal: Number(form.min_subtotal) || 0, applies_to: form.applies_to,
      target_id: form.applies_to === "all" ? null : (form.target_id || null),
      ends_at: form.ends_at ? new Date(form.ends_at + "T23:59:59").toISOString() : null,
      usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
      per_customer_limit: form.per_customer_limit === "" ? null : Number(form.per_customer_limit),
      stackable_with_loyalty: form.stackable_with_loyalty, active: form.active };
    try {
      if (editId) await opApi.put(`/tenant/promos/${editId}`, payload);
      else await opApi.post("/tenant/promos", payload);
      toast.success("Saved."); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete code ${p.code}?`)) return;
    try { await opApi.delete(`/tenant/promos/${p.id}`); load(); } catch (er) { toast.error(apiError(er.response?.data?.detail)); }
  };

  const label = (p) => p.type === "percent" ? `${p.value}% off` : p.type === "amount" ? `$${p.value} off` : "Free item";

  return (
    <div data-testid="promo-manager">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground max-w-lg">Discount codes customers can enter at checkout (Register &amp; online). Off by default they don't stack with loyalty rewards — tick "stackable" to allow both.</p>
        <Button size="sm" className="rounded-none gap-1.5" onClick={openNew} data-testid="promo-add-btn"><Plus className="h-4 w-4" /> New code</Button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground flex items-center gap-2 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        : rows.length === 0 ? <div className="border border-dashed border-border py-8 text-center text-sm text-muted-foreground" data-testid="promo-empty">No promo codes yet.</div>
        : (
        <div className="border border-border divide-y divide-border">
          {rows.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`promo-row-${p.id}`}>
              <Tag className="h-4 w-4 text-primary shrink-0" />
              <span className="font-mono font-semibold text-sm">{p.code}</span>
              <Badge variant="outline" className="rounded-none">{label(p)}</Badge>
              {p.applies_to !== "all" && <span className="text-xs text-muted-foreground">· {p.applies_to === "product" ? "1 product" : p.target_id}</span>}
              {p.stackable_with_loyalty && <Badge variant="outline" className="rounded-none text-emerald-700">stackable</Badge>}
              {!p.active && <Badge variant="outline" className="rounded-none text-muted-foreground">off</Badge>}
              {p.expired && <Badge variant="outline" className="rounded-none text-destructive">expired</Badge>}
              <div className="flex-1 text-right text-xs text-muted-foreground">used {p.times_used || 0}{p.usage_limit ? `/${p.usage_limit}` : ""}</div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`promo-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(p)} data-testid={`promo-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="promo-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit promo code" : "New promo code"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-auto pr-1">
            <div><Label className="text-xs">Code</Label><Input className="rounded-none mt-1 font-mono uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME10" data-testid="promo-code" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="promo-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">% off</SelectItem><SelectItem value="amount">$ off</SelectItem><SelectItem value="free_item">Free item</SelectItem></SelectContent>
                </Select>
              </div>
              {form.type !== "free_item" && (
                <div><Label className="text-xs">{form.type === "percent" ? "Percent" : "Amount ($)"}</Label><Input className="rounded-none mt-1" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="promo-value" /></div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Min. subtotal ($)</Label><Input className="rounded-none mt-1" type="number" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} data-testid="promo-min" /></div>
              <div><Label className="text-xs">Applies to</Label>
                <Select value={form.applies_to} onValueChange={(v) => setForm({ ...form, applies_to: v, target_id: "" })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="promo-applies"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Whole order</SelectItem><SelectItem value="category">A category</SelectItem><SelectItem value="product">One product</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {form.applies_to === "category" && (
              <div><Label className="text-xs">Category</Label>
                <Select value={form.target_id} onValueChange={(v) => setForm({ ...form, target_id: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="promo-target-cat"><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.applies_to === "product" && (
              <div><Label className="text-xs">Product</Label>
                <Select value={form.target_id} onValueChange={(v) => setForm({ ...form, target_id: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="promo-target-prod"><SelectValue placeholder="Choose product" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Expires</Label><Input className="rounded-none mt-1" type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="promo-expires" /></div>
              <div><Label className="text-xs">Total uses</Label><Input className="rounded-none mt-1" type="number" placeholder="∞" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} data-testid="promo-uses" /></div>
              <div><Label className="text-xs">Per customer</Label><Input className="rounded-none mt-1" type="number" placeholder="∞" value={form.per_customer_limit} onChange={(e) => setForm({ ...form, per_customer_limit: e.target.value })} data-testid="promo-per-cust" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.stackable_with_loyalty} onChange={(e) => setForm({ ...form, stackable_with_loyalty: e.target.checked })} data-testid="promo-stackable" /> Can stack on top of a loyalty reward</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="promo-active" /> Active</label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={save} disabled={saving} data-testid="promo-save">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save code</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
